import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignupForm } from '@/components/auth/SignupForm';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import heroImage from '@/assets/hero-antiques.jpg';

const DEMO_EMAIL = import.meta.env['VITE_DEMO_EMAIL'] as string | undefined;
const DEMO_PASSWORD = import.meta.env['VITE_DEMO_PASSWORD'] as string | undefined;

interface AuthPageProps {
  onAuthSuccess?: () => void;
  autoDemo?: boolean;
}

export function AuthPage({ onAuthSuccess, autoDemo = false }: AuthPageProps) {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [demoLoading, setDemoLoading] = useState(false);

  const handleSuccess = () => {
    if (onAuthSuccess) {
      onAuthSuccess();
      return;
    }
    // If opened as popup, close tab to return to original page
    if (window.opener) {
      window.close();
      // If close fails, navigate to /
      setTimeout(() => navigate('/', { replace: true }), 200);
      return;
    }
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    navigate(from, { replace: true });
  };

  const handleLogin = async (credentials: { email: string; password: string }) => {
    await login(credentials.email, credentials.password);
    handleSuccess();
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleDemoLogin = async () => {
    if (!DEMO_EMAIL || !DEMO_PASSWORD) return;
    setDemoLoading(true);
    try {
      await handleLogin({ email: DEMO_EMAIL, password: DEMO_PASSWORD });
    } finally {
      setDemoLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: (import.meta.env.VITE_SITE_URL || window.location.origin) + '/auth/callback',
        },
      });
    } catch {
      // Popup blocked or network error — finally resets loading
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (autoDemo && DEMO_EMAIL && DEMO_PASSWORD) {
      void handleDemoLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignup = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    businessName: string;
  }) => {
    const result = await signup(userData.email, userData.password, {
      first_name: userData.firstName,
      last_name: userData.lastName,
      business_name: userData.businessName,
    });
    if (result.requiresConfirmation) {
      throw new Error('Check your email to confirm your account before signing in.');
    }
    handleSuccess();
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - Hero Image */}
      <div className="relative hidden lg:flex lg:w-1/2">
        <img
          loading="lazy"
          src={heroImage}
          alt="Antiques collection showcasing the essence of flipping business"
          className="h-full w-full object-cover"
        />
        <div className="gradient-hero absolute inset-0 opacity-80"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-white">
          <div className="max-w-md text-center">
            <div className="mb-6 flex justify-center">
              <Logo variant="wordmark" size="lg" scheme="dark" />
            </div>
            <h1 className="mb-6 text-4xl font-bold">Welcome to For Visibility</h1>
            <p className="mb-4 text-lg opacity-90">
              Your comprehensive business operating system for antiques, thrift, and flipping
              success
            </p>
            <div className="space-y-2 text-sm opacity-75">
              <p>✓ Inventory Management</p>
              <p>✓ Point of Sale System</p>
              <p>✓ Profit Tracking</p>
              <p>✓ Customer Management</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="flex w-full items-center justify-center bg-fos-bg-base p-8 lg:w-1/2">
        <div className="w-full max-w-md space-y-4">
          {isLogin ? (
            <LoginForm onLogin={handleLogin} onSwitchToSignup={() => setIsLogin(false)} />
          ) : (
            <SignupForm onSignup={handleSignup} onSwitchToLogin={() => setIsLogin(true)} />
          )}

          {DEMO_EMAIL && DEMO_PASSWORD && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-fos-bg-base px-2 text-fos-text-muted">or</span>
              </div>
            </div>
          )}

          {DEMO_EMAIL && DEMO_PASSWORD && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleDemoLogin}
              disabled={demoLoading}
            >
              {demoLoading ? 'Logging in…' : 'Try Demo Account'}
            </Button>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-fos-bg-base px-2 text-fos-text-muted">or continue with</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {googleLoading ? 'Redirecting…' : 'Sign in with Google'}
          </Button>
        </div>
      </div>
    </div>
  );
}
